import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
  NgZone,
  isDevMode,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CdkFormGroup } from '@onepointfour-npm/cdk';
import { debounceTime, Subscription } from 'rxjs';
import { BoardCrud } from 'src/app/models/boardCrud';
import { CardEntity, Participant } from 'src/app/models/entities/card.entity';
import { Location } from '@angular/common';
import {
  BillableItemEntity,
  Page,
} from 'src/app/models/entities/project.entity';
import { SettingsService } from 'src/app/services/api/settings.service';
import { UserService } from 'src/app/services/api/user.service';
import { UserEntity } from 'src/app/models/entities/user.entity';
import { BoardEntity, Epic } from 'src/app/models/entities/board.entity';
import { Utils } from 'src/app/utils';
import { MatSelectChange } from '@angular/material/select';
import { TimeSheetEntity } from 'src/app/models/entities/timesheet.entity';
import { TimeSheetService } from 'src/app/services/api/timesheet.service';
import { DateHelperService } from 'src/app/services/dateHelper.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NotificationEntity } from 'src/app/models/entities/notification.entity';
import { CardService } from 'src/app/services/api/card.service';
import { CardActivityService } from 'src/app/services/api/card-activity.service';
import { CardHistoryService } from 'src/app/services/api/cardhistory.service';
import { ClientService } from 'src/app/services/api/clients.service';
import { NotificationService } from 'src/app/services/api/notifications.service';

import { SelectUserModalComponent } from '../select-user-modal/select-user-modal.component';
import { SharedStateService } from 'src/app/services/shared.state.service';
import { Timestamp } from '@angular/fire/firestore';
import { Lane } from 'src/app/models/lane';
import { CardHistoryEntity } from 'src/app/models/entities/cardhistory.entity';
import { HistoryService } from 'src/app/services/api/history.service';
import { ScoringService } from 'src/app/services/card.score.service';
import { TrackedFormControl } from '../trackFormControl';
import { user } from '@angular/fire/auth';
import { RepositoryComponentType } from 'src/app/models/entities/ai-agent-config.entity';

@Component({
  selector: 'app-card-details',
  templateUrl: './card-details.component.html',
  styleUrls: ['./card-details.component.scss'],
  standalone: false,
})
export class CardDetailsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() public data!: BoardCrud;
  @Input() public editMode: boolean = false;
  @Input() public chatHasFocus: boolean = false;
  @Input() public epics: Epic[] = [];
  @Input() public cardhistory: CardHistoryEntity[] = [];

  @Input() public selectedBillabelItem: BillableItemEntity | undefined;
  @Output() public noteChanged: EventEmitter<string> = new EventEmitter();
  @Output() public keyChanged: EventEmitter<string> = new EventEmitter();
  @Output() public usersLoaded: EventEmitter<UserEntity[]> = new EventEmitter();
  @Output() public itemSelect: EventEmitter<BillableItemEntity> =
    new EventEmitter();
  @Output() public nameHasChanged: EventEmitter<string> = new EventEmitter();
  @Output() public issuesChanged: EventEmitter<CardEntity[]> =
    new EventEmitter();
  @Output() public imageZoom: EventEmitter<string> = new EventEmitter<string>();
  @Output() public timeSheetStopped: EventEmitter<TimeSheetEntity> = new EventEmitter<TimeSheetEntity>();
  @Output() public timeSheetStarted: EventEmitter<TimeSheetEntity> = new EventEmitter<TimeSheetEntity>();
  @Output() public openTimeSheetTab: EventEmitter<boolean> = new EventEmitter<boolean>();

  saving: boolean = false;
  
  // Repository Component Types for dropdown
  public repositoryComponentTypes = [
    { value: 'ui', label: 'UI / Frontend' },
    { value: 'cms', label: 'CMS / Content Management' },
    { value: 'api', label: 'API / Backend' }
  ];

  public boardForm: CdkFormGroup = new CdkFormGroup({
    dateCreated: new TrackedFormControl(new Date(), null, null, false),
    updatedAt: new TrackedFormControl(new Date(), null, null, false),
    key: new TrackedFormControl(null, null, null, false),
    code: new TrackedFormControl(null, null, null, false),
    column: new TrackedFormControl(null, null, null, false),
    lane: new TrackedFormControl(null, null, null, true),
    previousColumn: new TrackedFormControl(null, null, null, false),
    createdAt: new TrackedFormControl(null, null, null, false),
    name: new TrackedFormControl(null, [Validators.required], null, true),
    // part: new TrackedFormControl(null, null, null, true),
    assignedTo: new TrackedFormControl(null, null, null, true),
    assignedToRole: new TrackedFormControl(null, null, null, true),
    priority: new TrackedFormControl(null, null, null, true),
    parent: new TrackedFormControl(null, null, null, false),
    description: new TrackedFormControl(null, null, null, false),
    item: new TrackedFormControl(null, null, null, false),
    parentTmp: new TrackedFormControl(null, null, null, false),
    type: new TrackedFormControl('Task', null, null, true),
    status: new TrackedFormControl(null, null, null, true),
    designLink: new TrackedFormControl(null, null, null, true),
    testLink: new TrackedFormControl(null, null, null, true),
    dueDate: new TrackedFormControl(null, null, null, true),
    reporter: new TrackedFormControl(null, null, null, false),
    projectId: new TrackedFormControl(null, null, null, false),
    page: new TrackedFormControl(null, null, null, false),
    done: new TrackedFormControl(false, null, null, false),
    pageImageUrl: new TrackedFormControl(null, null, null, false),
    section: new TrackedFormControl(null, null, null, false),
    epic: new TrackedFormControl(null, null, null, true),
    thumb: new TrackedFormControl(null, null, null, false),
    files: new TrackedFormControl([], null, null, false),
    orderInColumn: new TrackedFormControl(null, null, null, false),
    labels: new TrackedFormControl([], null, null, true),
    dor: new TrackedFormControl([], null, null, true),
    repositoryComponents: new TrackedFormControl([], null, null, true),
    participants: new TrackedFormControl([], null, null, false),
    lastDueDateChange: new TrackedFormControl(null, null, null, false),
    storyPoints: new TrackedFormControl(null, null, null, true),
    environment: new TrackedFormControl(null, null, null, true),
    module: new TrackedFormControl(null, null, null, true),
    component: new TrackedFormControl(null, null, null, true),
    cannotReproduce: new TrackedFormControl(false, null, null, true),
    agentMetadata: new TrackedFormControl(null, null, null, false), // Preserve AI agent metadata
    updated_at: new TrackedFormControl(new Date(), null, null, false), // Required by AI agent schema
  });
  selectedUserRoles: string[] = [];

  public fileUploaded: boolean = false;
  public selectedPage: Page | undefined;
  public content!: string;
  public loadingContent: boolean = true;
  public contentPasted: boolean = false;
  public dialogCloseAction:
    | 'delete'
    | 'save'
    | 'edit'
    | 'cancel'
    | 'editStatus' = 'cancel';
  public createdHere: boolean = false;
  public pauseSave: boolean = false;
  public dataChanged: boolean = false;
  public uploadFolder: string = '';

  private firstLoad: boolean = true;

  public openTimeSheet: TimeSheetEntity | undefined;
  public parentItem: CardEntity | undefined;
  public children: CardEntity[] = [];
  public settingData: boolean = false;

  public enhancementAdded: boolean = false;
  public codes: { code: string; board: string }[] = [];
  public dueDateText: string = '';
  public dueDateStatus: 'positive' | 'negative' | 'neutral' = 'neutral';

  private timerSubscription: Subscription | undefined;
  private subscriptions: Subscription[] = [];
  private trackedCardKey: string | null = null;

  constructor(
    public sharedStateService: SharedStateService,
    public userService: UserService,
    public cardService: CardService,
    public dialogRef: MatDialogRef<any>,
    private dialog: MatDialog,
    private timeSheetService: TimeSheetService,
    private dateHelper: DateHelperService,
    private snackbar: MatSnackBar,
    private scoringService: ScoringService,
    private ngZone: NgZone,
    private notificationService: NotificationService,
    private location: Location,
    private history: HistoryService,
    private cardActivity: CardActivityService,
    public settings: SettingsService
  ) {
    this.initializeFormSubscriptions();

    this.cardService.getAllCodesFromAllBoards().subscribe((codes) => {
      this.codes = codes;
    });
  }

  ngOnInit(): void {
    this.initializeBoardFormSubscriptions();

    this.subscriptions.push(
      this.boardForm.controls['key'].valueChanges.subscribe(cardKey => {
        this.updateTimerSubscription(cardKey);
        this.handleCardKeyChangeForActivity(cardKey);
      })
    );

    if (this.boardForm.controls['key'].value) {
      this.updateTimerSubscription(this.boardForm.controls['key'].value);
      this.handleCardKeyChangeForActivity(this.boardForm.controls['key'].value);
    }
  }

  private handleCardKeyChangeForActivity(cardKey: string | null): void {
    if (this.trackedCardKey && this.trackedCardKey !== cardKey) {
      this.cardActivity.endSession(this.trackedCardKey);
      this.trackedCardKey = null;
    }
    if (cardKey && cardKey !== this.trackedCardKey) {
      const cardName = this.boardForm.controls['name']?.value || '';
      const boardId = this.boardForm.controls['boardKey']?.value || this.data?.board?.key || '';
      this.cardActivity.startSession(cardKey, boardId, cardName);
      this.trackedCardKey = cardKey;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.data = changes['data'].currentValue;
      if (this.data) {
        this.settingData = true;

        this.sharedStateService
          .initializeData(this.data, this.boardForm, this.location, this.epics)
          .then(() => {
            this.settingData = false;
            this.children = this.data.cards.filter(
              (c) => c.parent === this.boardForm.controls['key'].value
            );
            this.updateDueDateDisplay(this.boardForm.controls['dueDate'].value);

            // Set reporter to current user when creating a new card (not in edit mode)
            if (!this.sharedStateService.editMode && !this.boardForm.controls['key'].value) {
              this.boardForm.controls['reporter'].setValue(this.userService.currentUser.key);
            }

            // Manually trigger form validation update to ensure UI reflects correct state
            // This fixes timing issues where form becomes valid but UI doesn't update
            setTimeout(() => {
              this.boardForm.updateValueAndValidity({ emitEvent: true });
            }, 100);
          });
      }
    }
    if (changes['selectedBillabelItem']) {
      this.boardForm.controls['item'].setValue(this.selectedBillabelItem?.name);
    }
  }

  ngOnDestroy(): void {
    if (this.trackedCardKey) {
      this.cardActivity.endSession(this.trackedCardKey);
      this.trackedCardKey = null;
    }

    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private initializeFormSubscriptions(): void {
    
    this.boardForm.controls['parentTmp'].valueChanges
      .pipe(debounceTime(1000))
      .subscribe((value) => {
        if (value && value.length > 0 && this.sharedStateService.cards) {
          this.sharedStateService.filteredCards =
            this.sharedStateService.cards.filter(
              (item: any) =>
                item.name &&
                value &&
                item.name.toLowerCase().includes(value.toLowerCase()) &&
                item.key !== this.boardForm.controls['key'].value
            );
        }
      });
  }

  private initializeBoardFormSubscriptions(): void {
    this.boardForm.controls['assignedTo'].valueChanges
      .pipe(debounceTime(1000))
      .subscribe((userKey) => {
        if (!this.sharedStateService.firstLoad) {
          this.handleAssignedToChange(userKey);
        } else {
          this.setRoles(userKey);
        }
      });

    this.boardForm.controls['assignedToRole'].valueChanges
      .pipe(debounceTime(1000))
      .subscribe((role) => {
        let userKey = this.boardForm.controls['assignedTo'].value;

        if (!this.sharedStateService.firstLoad) {
          this.handleAssignedToChange(userKey, role);
        } else {
          this.setRoles(userKey);
        }
      });
    this.boardForm.controls['dueDate'].valueChanges
      .pipe(debounceTime(1000))
      .subscribe((dueDate) => {
        if (!this.sharedStateService.firstLoad) {
          this.boardForm.controls['lastDueDateChange'].setValue(
            Timestamp.now()
          );
          this.updateDueDateDisplay(dueDate);
        } else {
          this.updateDueDateDisplay(dueDate);
        }
      });

    let initialColumn = this.boardForm.controls['lane'].value;
    this.boardForm.controls['lane'].valueChanges
      .pipe(debounceTime(100))
      .subscribe((lane) => {
        this.boardForm.controls['previousColumn'].setValue(initialColumn);
        initialColumn = lane;
        if (this.editMode) {
          this.dialogCloseAction = 'editStatus';
        }
        this.handleLaneChange(lane, false);
      });

    if (this.boardForm.controls['parent'].value) {
      this.parentItem = this.sharedStateService.cards.find(
        (g) =>
          g.key == this.boardForm.controls['parent'].value &&
          g.key !== this.boardForm.controls['key'].value
      );
    }
  }

  private handleAssignedToChange(userKey: string, role?: string): void {
    this.setRoles(userKey);

    // Use sharedStateService.card for key — boardForm.value may have null key
    const cardForNotification = { ...this.boardForm.value, key: this.boardForm.value.key || this.sharedStateService.card?.key };
    this.cardService
      .handleAssignedToChange(
        userKey,
        cardForNotification,
        this.sharedStateService.currentUser.key,
        role
      )
      .then((participants: Participant[]) => {
        this.boardForm.controls['participants'].setValue(participants);
      });
  }

  private setRoles(userKey: string) {
    const user = this.sharedStateService.users.find((g) => g.key === userKey);
    this.selectedUserRoles = user?.roles || [];

    if (this.selectedUserRoles.length === 1) {
      this.boardForm.controls['assignedToRole'].setValue(
        this.selectedUserRoles[0],
        { emitEvent: false }
      );
      this.boardForm.controls['assignedToRole'].setValidators([
        Validators.required,
      ]);
      this.boardForm.controls['assignedToRole'].updateValueAndValidity({
        emitEvent: false,
      });
    } else {
      this.boardForm.controls['assignedToRole'].setValidators([
        Validators.required,
      ]);
      this.boardForm.controls['assignedToRole'].updateValueAndValidity({
        emitEvent: false,
      });
    }

    if (userKey === null) {
      // Clear both value and validators
      this.boardForm.controls['assignedToRole'].setValue(null, {
        emitEvent: false,
      });
      this.boardForm.controls['assignedToRole'].clearValidators();
      this.boardForm.controls['assignedToRole'].updateValueAndValidity({
        emitEvent: false,
      });
    }
  }

  private handleLaneChange(lane: string, process: boolean = false): void {
    this.cardService
      .handleLaneChange(
        lane,
        this.boardForm.value,
        this.sharedStateService.board,
        process
      )
      .then((user: UserEntity | null | string) => {
        if (user !== null && user !== 'retain') {
          const userNew = user as UserEntity;
          this.boardForm.controls['assignedTo'].setValue(userNew?.key, {
            emitEvent: false,
          });
        }
        if (user == null) {
          this.boardForm.controls['assignedTo'].setValue(null, {
            emitEvent: false,
          });
        }
      })
      .catch(() => {
          this.boardForm.controls['lane'].setValue(
            this.boardForm.controls['previousColumn'].value,
            { emitEvent: false }
          );

      });
  }

  public fileAdded($event: string) {
    this.fileUploaded = true;
    var files: string[] = this.boardForm.controls['files'].value;
    files.push($event);
    this.boardForm.controls['files'].setValue(files);
  }

  public attachmentsChanged($event: any[]) {
    // Update the form control with the new attachments array after deletion
    this.boardForm.controls['files'].setValue($event);
  }

  public setCard(card: CardEntity): void {
    this.sharedStateService.card = card;
    this.boardForm.patchValue(card);
    this.updateDueDateDisplay(card.dueDate);
  }

  private updateDueDateDisplay(dueDate: Date | null): void {
    if (dueDate) {
      const dueDateInfo = this.dateHelper.formatDueDate(dueDate);
      this.dueDateText = dueDateInfo.text;
      this.dueDateStatus = dueDateInfo.status;
    } else {
      this.dueDateText = '';
      this.dueDateStatus = 'neutral';
    }
  }

  public selectItem(item: any): void {
    this.parentItem = item;
    this.boardForm.controls['parentTmp'].setValue(item.name);
    this.boardForm.controls['parent'].setValue(item.key);
  }

  public selectParent(): void {
    if (this.parentItem) {
      this.sharedStateService.card = this.parentItem;
      this.children = this.sharedStateService.cards.filter(
        (g) => g.parent == this.sharedStateService.card?.key
      );
      this.parentItem = this.sharedStateService.cards.find(
        (g) => g.key == this.sharedStateService.card?.parent
      );
      this.boardForm.controls['parentTmp'].setValue(this.parentItem?.name);
      this.boardForm.patchValue(this.sharedStateService.card);
    }
  }

  public pauseSaveFired($event: boolean): void {
    this.ngZone.run(() => {
      this.pauseSave = $event;
    });
  }

  public itemSelected($event: MatSelectChange): void {
    const itemName = $event.value;
    const item = this.sharedStateService.billableItems.find(
      (g) => g.name == itemName
    );
    if (item) {
      this.itemSelect.emit(item);
    }
  }

  public selectChildItem(child: any): void {
    this.sharedStateService.card = child.value;
    this.children = this.sharedStateService.cards.filter(
      (g) => g.parent == this.sharedStateService.card?.key
    );
    this.parentItem = this.sharedStateService.cards.find(
      (g) => g.key == this.sharedStateService.card?.parent
    );
    this.boardForm.controls['parentTmp'].setValue(this.parentItem?.name);
    this.boardForm.patchValue(this.sharedStateService.card);
  }

  public removeItem(child: any): void {
    child.parent = null;
    this.cardService.updateCard(
      this.sharedStateService.card.key,
      child,
      this.sharedStateService.board.key
    );
    this.children = this.sharedStateService.cards.filter(
      (g) => g.parent == this.sharedStateService.card?.key
    );
    this.issuesChanged.emit(this.children);
  }

  public getReporterName(reporterKey: string): string {
    if (!reporterKey || !this.sharedStateService.users) {
      return '';
    }
    
    const reporter = this.sharedStateService.users.find(user => user.key === reporterKey);
    return reporter ? reporter.name : '';
  }

  public isLaneTypeBug(): boolean {
    if (!this.sharedStateService.board || !this.sharedStateService.board.lanes || !this.boardForm) {
      return false;
    }
    
    const currentLaneKey = this.boardForm.get('lane')?.value;
    if (!currentLaneKey) {
      return false;
    }
    
    const currentLane = this.sharedStateService.board.lanes.find(lane => lane.key === currentLaneKey);
    return currentLane?.laneType === 'bug';
  }

  public save(close: boolean = false): void {
    if (this.boardForm.valid && !this.saving) {
      // The HTML editor sets pauseSave=true while it is compressing+uploading
      // a freshly-pasted image. If the user hits Save before the upload
      // finishes, the description still contains the raw base64 data URI
      // and would blow past Firestore's 1 MB per-property cap. Tell the
      // user and bail — the upload's onload will clear pauseSave shortly.
      if (this.pauseSave) {
        this.snackbar.open(
          'Still uploading an image — please try again in a moment.',
          'Close',
          { duration: 3000 }
        );
        return;
      }

      // Firestore caps a single property at 1,048,487 bytes. Reject oversize
      // descriptions up-front so users get a clear message instead of a
      // generic "Error creating card" from Firestore. 900 KB leaves headroom
      // for the rest of the document.
      const description: string = this.boardForm.controls['description'].value || '';
      const descriptionBytes = new Blob([description]).size;
      if (descriptionBytes > 900_000) {
        this.snackbar.open(
          `Description is too large (${Math.round(descriptionBytes / 1024)} KB). Please remove or replace large embedded images.`,
          'Close',
          { duration: 5000 }
        );
        return;
      }

      this.saving = true;

      const participants = this.boardForm.controls['participants'].value;

      // Make sure the reporter is set for new cards
      if (!this.sharedStateService.editMode && !this.boardForm.controls['reporter'].value) {
        this.boardForm.controls['reporter'].setValue(this.userService.currentUser.key);
      }

      if (
        !!this.boardForm.value.assignedTo &&
        (participants.length === 0 ||
          (this.boardForm.value.assignedTo !==
            this.sharedStateService.orginalAssignedTo &&
            participants.indexOf(this.boardForm.value.assignedTo) == -1))
      ) {
        participants.push(this.boardForm.value.assignedTo);
        this.boardForm.controls['participants'].setValue(participants);
      }

      if (this.settingData === false) {
        if (this.boardForm.controls['parent'].value === undefined) {
          this.settingData = true;
          this.boardForm.controls['parent'].setValue(null);
          setTimeout(() => {
            this.settingData = false;
          }, 500);
        }

        if (this.sharedStateService.editMode) {
          const cardKey = this.boardForm.controls['key'].value;
          if (!cardKey) {
            console.error('Cannot save card: Card key is missing');
            this.snackbar.open('Error: Unable to save card. Please try refreshing the page.', 'Close', {
              duration: 3000,
            });
            this.saving = false;
            return;
          }

          var newValue = this.boardForm.value;
          newValue.updatedAt = new Date();
          newValue.updated_at = new Date(); // Required by AI agent schema
          newValue.triggeredByUserSave = true; // Flag to indicate explicit user save
          
          console.log('🚀 Card save triggered:', {
            cardId: cardKey,
            assignedTo: newValue.assignedTo,
            triggeredByUserSave: newValue.triggeredByUserSave,
            lane: newValue.lane,
            type: newValue.type,
            boardId: this.sharedStateService.board?.key
          });
          // Check if AI agent should be triggered
          this.sharedStateService.checkAndTriggerAI(newValue);
          
          // Check if build server should be triggered
          this.sharedStateService.checkAndTriggerBuildServer(newValue);
          
          delete newValue.minify;
          delete newValue.expanded;
          delete newValue.hide;
          newValue.item == undefined ? null : newValue.item;
          if (!this.createdHere) {
            if (this.dialogCloseAction != 'editStatus') {
              this.dialogCloseAction = 'edit';
            }
          }

         this.sharedStateService.updateCardDoneStatus(newValue, this.boardForm.controls['lane'].value);
          this.data.card = newValue;

          //check if the lane changed
          if (this.sharedStateService.card.lane != newValue.lane) {
            if (this.sharedStateService.board)
              this.cardService.processLaneChange(
                newValue,
                newValue.lane,
                this.sharedStateService.board
              );
          }

          newValue = Utils.convertDatesToTimestamps(newValue);

          Utils.setUndefinedToNull(newValue);

          this.cardService
            .updateCard(
              this.sharedStateService.board.key,
              newValue,
              cardKey
            )
            .then(() => {
              // Associate any pending timesheets with this card
              this.associatePendingTimesheetsWithCard(cardKey);

              if (close) {
                this.dialogRef.close({
                  status: this.dialogCloseAction,
                  card: this.boardForm.value,
                });
              }

              this.history.saveHistory(
                this.boardForm,
                this.sharedStateService.oldValues,
                this.userService.currentUser.key,
                this.content,
                this.sharedStateService.lookupDictionary
              );

              this.sharedStateService.sendNotifications(
                this.notificationService
              );

              // Reset form dirty state after successful save
              this.sharedStateService.dataChanged = false;

              // Mark the Angular form as pristine
              this.boardForm.markAsPristine();
              this.boardForm.markAsUntouched();

              // Update oldValues with current form values
              Object.keys(this.boardForm.controls).forEach((controlName) => {
                const control = this.boardForm.get(controlName) as any;
                this.sharedStateService.oldValues.set(controlName, control?.value);

                // Reset the changed flag on TrackedFormControls
                if (control && typeof control.resetChanged === 'function') {
                  control.resetChanged();
                }
              });

              // Update the card entity in SharedStateService with the new values
              if (this.sharedStateService.card) {
                Object.keys(this.boardForm.controls).forEach((controlName) => {
                  const control = this.boardForm.get(controlName);
                  if (control && this.sharedStateService.card) {
                    this.sharedStateService.card[controlName] = control.value;
                  }
                });
              }

              this.snackbar.open('Card Updated', 'Close', {
                duration: 2000,
              });
              this.saving = false;
            })
            .catch((error) => {
              console.error('Error updating card:', error);
              this.snackbar.open('Error updating card', 'Close', {
                duration: 2000,
              });
              this.saving = false;
            });
        } else {
          if (!this.enhancementAdded) {
            var newValue = this.boardForm.value;
            newValue.item = newValue.item === undefined ? null : newValue.item;
            this.dialogCloseAction = 'save';
            this.history.saveCardHistory(
              'Card Created',
              'Card Created',
              '',
              '',
              this.boardForm,
              this.userService.currentUser.name,
              '',          
            );
            this.data.card = newValue;
            newValue.createdAt = new Date();
            newValue = Utils.convertDatesToTimestamps(newValue);
            newValue.item = newValue.item === undefined ? null : newValue.item;
            this.cardService
              .createCardItem(this.sharedStateService.board, newValue)
              .subscribe({
                next: (card: CardEntity) => {
                  // Associate any pending timesheets with this new card
                  this.associatePendingTimesheetsWithCard(card.key);
                  
                  if (close) {
                    this.dialogRef.close({
                      status: this.dialogCloseAction,
                      card,
                    });
                  }

                  this.sharedStateService.sendNotifications(
                    this.notificationService
                  );

                  this.history.saveHistory(
                    this.boardForm,
                    this.sharedStateService.oldValues,
                    this.userService.currentUser.key,
                    this.content,
                    this.sharedStateService.lookupDictionary
                  );
                  this.boardForm.controls['key'].setValue(card.key);
                  this.keyChanged.emit(card.key);
                  this.sharedStateService.editMode = true;

                  // Reset form dirty state after successful card creation
                  this.sharedStateService.dataChanged = false;

                  // Mark the Angular form as pristine
                  this.boardForm.markAsPristine();
                  this.boardForm.markAsUntouched();

                  // Update oldValues with current form values
                  Object.keys(this.boardForm.controls).forEach((controlName) => {
                    const control = this.boardForm.get(controlName) as any;
                    this.sharedStateService.oldValues.set(controlName, control?.value);

                    // Reset the changed flag on TrackedFormControls
                    if (control && typeof control.resetChanged === 'function') {
                      control.resetChanged();
                    }
                  });

                  // Update the card entity in SharedStateService with the new values
                  if (this.sharedStateService.card) {
                    Object.keys(this.boardForm.controls).forEach((controlName) => {
                      const control = this.boardForm.get(controlName);
                      if (control && this.sharedStateService.card) {
                        this.sharedStateService.card[controlName] = control.value;
                      }
                    });
                  }

                  this.saving = false;
                },
                error: (error) => {
                  console.error('Error creating card:', error);
                  this.snackbar.open('Error creating card', 'Close', {
                    duration: 2000,
                  });
                  this.saving = false;
                },
              });
          }
        }

        if (this.enhancementAdded) {
          this.enhancementAdded = false;
          this.dialogCloseAction = 'save';

          // Create the issue/enhancement card directly instead of calling nameChanged()
          // This ensures proper handling of the close parameter and prevents duplication
          var newValue = this.boardForm.value;
          newValue.item = newValue.item === undefined ? null : newValue.item;
          newValue.createdAt = new Date();
          newValue = Utils.convertDatesToTimestamps(newValue);

          this.cardService
            .createCardItem(this.sharedStateService.board, newValue)
            .subscribe({
              next: (card: CardEntity) => {
                // Associate any pending timesheets with this new card
                this.associatePendingTimesheetsWithCard(card.key);

                this.boardForm.controls['key'].setValue(card.key);
                this.keyChanged.emit(card.key);
                this.editMode = true;
                this.sharedStateService.editMode = true; // Fix: set sharedStateService.editMode too

                this.sharedStateService.sendNotifications(this.notificationService);

                // Reset form dirty state after successful card creation
                this.sharedStateService.dataChanged = false;
                this.boardForm.markAsPristine();
                this.boardForm.markAsUntouched();

                this.snackbar.open('Card Created', 'Close', {
                  duration: 2000,
                });

                this.saving = false;

                // Close dialog if close parameter was true
                if (close) {
                  this.dialogRef.close({
                    status: this.dialogCloseAction,
                    card: card,
                  });
                }
              },
              error: (error) => {
                console.error('Error creating issue/enhancement card:', error);
                this.snackbar.open('Error creating card', 'Close', {
                  duration: 2000,
                });
                this.saving = false;
              },
            });
        }
      }
    }
  }
  
  /**
   * Associates any pending timesheets with the newly saved card
   * @param cardKey The key of the card to associate the timesheets with
   */
  private associatePendingTimesheetsWithCard(cardKey: string): void {
    if (!cardKey || !this.sharedStateService.pendingTimesheets?.length) {
      return; // No card key or no pending timesheets
    }
    
    // Get the name of the card
    const cardName = this.boardForm.controls['name'].value;
    console.log('Using card name for pending timesheets:', cardName);
    
    // Process each pending timesheet
    this.sharedStateService.pendingTimesheets.forEach(timesheet => {
      if (timesheet && timesheet.key && this.userService.currentUser?.key) {
        // Update timesheet with card info
        const updatedTimesheet = {
          ...timesheet,
          card: cardKey,
          notes: timesheet.notes || cardName // Use the existing notes or set to card name
        };
        
        console.log(`Updating timesheet ${timesheet.key} notes to:`, updatedTimesheet.notes);
        
        // Save the updated timesheet
        this.timeSheetService.update(
          updatedTimesheet,
          this.userService.currentUser.key,
          timesheet.key
        ).then(() => {
          console.log(`Associated timesheet ${timesheet.key} with card ${cardKey}`);
        }).catch(error => {
          console.error(`Error updating timesheet ${timesheet.key}:`, error);
        });
      }
    });
    
    // Clear the pending timesheets after processing
    this.sharedStateService.pendingTimesheets = [];
  }

  public nameChanged() {
    if (!this.saving && this.enhancementAdded == false) {
      if (!this.boardForm.controls['key'].value) {
        this.saving = true;

        // Set reporter to current user when creating a card via name change
        if (!this.boardForm.controls['reporter'].value) {
          this.boardForm.controls['reporter'].setValue(this.userService.currentUser.key);
        }

        this.cardService
          .createCardItem(this.sharedStateService.board, this.boardForm.value)
          .subscribe({
            next: (card: CardEntity) => {
              this.boardForm.controls['key'].setValue(card.key);
              this.keyChanged.emit(card.key);
              this.editMode = true;
              this.sharedStateService.editMode = true; // Fix: also set sharedStateService.editMode
              this.saving = false;

            },
            error: (error) => {
              console.error('Error creating card:', error);
              this.saving = false;
            }
          });
      }
    }
  }
  public delete(): void {
    this.cardService.delete(
      this.sharedStateService.board.key,
      this.boardForm.controls['key'].value
    );
    this.dialogRef.close({ status: 'delete', card: this.boardForm.value });
  }

  public addChild(type: 'Issue' | 'Enhancement'): void {
    this.editMode = false;
    this.sharedStateService.editMode = false;
    this.enhancementAdded = true;
    this.sharedStateService.firstLoad = true;
    this.createdHere = true;

    this.boardForm.controls['type'].setValue(type);
    this.boardForm.controls['column'].setValue('To-Do');
    this.boardForm.controls['parent'].setValue(
      this.boardForm.controls['key'].value
    );
    this.boardForm.controls['parentTmp'].setValue(
      this.boardForm.controls['name'].value
    );
    
    // Set reporter to current user for new child cards
    this.boardForm.controls['reporter'].setValue(this.userService.currentUser.key);

    if (type == 'Issue') {
      this.updateContentEditor(
        `Fix issue for ${this.boardForm.controls['name'].value}: <br>`
      );
    } else {
      this.updateContentEditor(
        `Create enhancement for ${this.boardForm.controls['name'].value}: <br>`
      );
    }

    this.boardForm.controls['key'].setValue(null);
    this.boardForm.controls['name'].setValue(
      `${this.boardForm.controls['name'].value} - ${type}`
    );
    setTimeout(() => {
      this.sharedStateService.firstLoad = false;
    }, 1000);
  }

  public updateContentEditor($event: any): void {
    this.boardForm.controls['description'].setValue($event);
  }

  public timerStarted($event: TimeSheetEntity): void {
    this.openTimeSheet = $event;
  }

  public timerStopped(item: TimeSheetEntity): void {
    if (item) {
      this.timeSheetStopped.emit(item);
      this.openTimeSheet = undefined;
    }
  }

  public stopTimer(): void {
    if (this.openTimeSheet) {
      // Check if the timesheet is valid before stopping it
      // If the form is invalid, we need to open the timesheet tab
      // for the user to complete required fields
      
      // First check if the openTimeSheet has all required fields 
      // (billableItem, item, from)
      const isValid = this.openTimeSheet.billableItem && 
                      this.openTimeSheet.item && 
                      this.openTimeSheet.from;
      
      if (isValid) {
        // If valid, stop the timer normally
        this.timerStopped(this.openTimeSheet);
      } else {
        // If invalid, emit an event to open the timesheet tab
        this.openTimeSheetTab.emit(true);
      }
    }
  }

  /**
   * Component options from the board's repository config (for the Component dropdown).
   * Uses the repo's "Owner/Name" as the value — unambiguous even when several components
   * share a type — which is what the CI deploy-hook matches against.
   */
  public get repoComponentTypes(): string[] {
    const board = this.sharedStateService.board as any;
    const comps = board?.automationConfig?.sharedConfig?.repositoryComponents
      || board?.repositoryComponents || [];
    return comps
      .map((c: any) => String(c?.repositoryUrl || '')
        .replace(/\.git$/, '').replace(/\/+$/, '')
        .split('/').slice(-2).join('/'))
      .filter((v: string) => v.includes('/'));
  }

  public async share(): Promise<void> {
    try {
      const tenantId = this.userService.currentTenantId;
      const code = this.sharedStateService.card?.code || this.boardForm.value.code;

      // Prefer the short form: /s/{tenantToken}/{cardCode}. The /s route
      // resolves the tenant + card and the unfurl function serves rich
      // link previews for it.
      let link: string | null = null;
      if (tenantId && code) {
        const details = await this.userService.getTenantDetails(tenantId);
        if (details?.shortToken) {
          link = `${location.origin}/s/${details.shortToken}/${code}`;
        }
      }

      if (!link) {
        // Fallback: current URL with the tenant embedded so the recipient's
        // auth guard (which honors ?t=<tenantId>) switches them into the
        // right tenant even if they're logged into a different one.
        const url = new URL(location.href);
        if (tenantId && !url.searchParams.has('t')) {
          url.searchParams.set('t', tenantId);
        }
        link = url.toString();
      }

      await navigator.clipboard.writeText(link);
      this.snackbar.open('Link copied to clipboard', 'Close', {
        duration: 2000,
      });
    } catch (error) {
      console.error('Failed to copy link to clipboard', error);
    }
  }

  public startTimer(): void {
    // Create a placeholder timesheet entry with minimal information
    const newTimesheet = new TimeSheetEntity();
    
    // Set the card name (even for unsaved cards)
    const cardName = this.boardForm.controls['name'].value || '';
    console.log('Setting timesheet notes from card name:', cardName);
    newTimesheet.notes = cardName;
    
    // Set the current timestamp
    const currentTimestamp = new Date().toLocaleTimeString('en-ZA', {
      hour12: false,
      hour: 'numeric',
      minute: 'numeric',
    });
    
    // If we have a card key, use it, but it's not required
    if (this.boardForm.controls['key'].value) {
    newTimesheet.card = this.boardForm.controls['key'].value;
    }
    
    // Immediately set openTimeSheet to show the Stop Timer button
    this.openTimeSheet = {
      ...newTimesheet,
      from: currentTimestamp,
      to: ''
    };
    
    // Emit the event for the cardTabs component to handle
    console.log('Starting timer without saving card:', this.openTimeSheet);
    this.timeSheetStarted.emit(this.openTimeSheet);
    
    // Open the timesheet tab so the user can select project and item
    this.openTimeSheetTab.emit(true);
  }

  private updateTimerSubscription(cardKey: string): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    
    if (cardKey) {
      this.timerSubscription = this.timeSheetService
        .getRunningTimer(cardKey, this.userService.currentUser.key)
        .subscribe(timer => {
          this.openTimeSheet = timer || undefined;
        });
      
      this.subscriptions.push(this.timerSubscription);
    }
  }

  public onNameBlur(): void {
    // Get the current name value
    const name = this.boardForm.controls['name'].value;
    if (name) {
      // Update the uploadFolder for consistent behavior with valueChanges
      this.uploadFolder = `${this.data.board.name}/${name}`;
      // Emit the name change event
      this.nameHasChanged.emit(name);
    }
  }
}
